import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { ArrowIcon, ChevronIcon, money } from './widgetParts';

// The wide, low version of the widget (layout=wide). Same features as the classic one - careers by sector, the career details
// popup, Skilldar, the immersive video, the AI chat - arranged for a banner-shaped space on a college's page:
//
//   Careers for Electrical students                         Careers   Video   Ask our AI
//   ▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇  ▇▇▇▇▇▇▇▇▇▇  ▇▇▇▇▇▇  ▇▇▇▇        <- one segment per sector, sized by its careers
//   ● Electrical 6   ● Carpentry 3   ● Roofing 3                                   SKILLDAR
//   ─────────────────────────────────────────────────────────                      (chart)
//   CCTV Installer →      Fire Alarm Engineer →      Quarry Plant Operator →
//   £33,792 · 1-2 years   £31,450 · 1-2 years        £29,100 · 2-3 years
//
// It is deliberately colourless: white, ink and hairlines, so that it sits on any college's site. The only colour is the
// sectors' own, as thin markers, and the college's accent as the underline of the active tab.
const STYLES = `
.tsw-w { --ink:#12151c; --muted:#596173; --line:#e4e6eb; --wash:#f5f6f8; --accent:#f9a748; color:var(--ink); background:#fff; font-family:inherit; text-align:left; box-sizing:border-box; display:flex; flex-direction:column; width:100%; }
.tsw-w *, .tsw-w *::before, .tsw-w *::after { box-sizing:border-box; }
.tsw-w { overflow-x:clip; }
.tsw-w.is-framed { border-radius:12px; box-shadow:0 1px 2px rgba(18,21,28,.06), 0 12px 32px -12px rgba(18,21,28,.18); }
.tsw-w { min-height:400px; }
.tsw-w.is-narrow { min-height:0; }
.tsw-w.is-fill { height:100%; }
.tsw-w-body { display:flex; flex:1 1 auto; min-height:0; }
.tsw-w.is-narrow .tsw-w-body { flex-direction:column; }
.tsw-w-main { flex:1 1 auto; min-width:0; padding:16px 28px 12px; display:flex; flex-direction:column; min-height:0; }
.tsw-w-side { flex:0 0 260px; border-left:1px solid var(--line); padding:14px 0 10px; display:flex; flex-direction:column; align-items:stretch; min-height:0; }
.tsw-w.is-narrow .tsw-w-main { padding:18px 16px 14px; }
.tsw-w.is-narrow .tsw-w-side { flex:0 0 auto; border-left:0; border-top:1px solid var(--line); padding:18px 16px 10px; }

/* with a fixed height only the list of careers gives way (it scrolls); the bar, the sectors and the pager keep their size */
.tsw-w-main > * { flex-shrink:0; }
.tsw-w.is-fill .tsw-w-careers { flex:1 1 auto; min-height:0; overflow-y:auto; scrollbar-width:thin; }
.tsw-w-head { display:flex; align-items:baseline; justify-content:space-between; gap:16px; flex-wrap:wrap; }
.tsw-w-title { margin:0; text-align:left; text-wrap:balance; font-size:19px; line-height:1.25; font-weight:650; letter-spacing:-.01em; }
.tsw-w-tabs { display:flex; gap:20px; }
.tsw-w-tab { appearance:none; background:none; border:0; padding:4px 0 6px; font:inherit; font-size:13.5px; color:var(--muted); cursor:pointer; border-bottom:2px solid transparent; transition:color .15s, border-color .15s; }
.tsw-w-tab:hover { color:var(--ink); }
.tsw-w-tab[aria-selected="true"] { color:var(--ink); font-weight:600; border-bottom-color:var(--accent); }

.tsw-w-spectrum { display:flex; gap:2px; flex:0 0 4px; height:4px; margin:10px 0 8px; overflow:hidden; }
.tsw-w-seg { appearance:none; border:0; padding:0; min-width:2px; border-radius:2px; cursor:pointer; opacity:.55; filter:grayscale(1); transition:opacity .25s, filter .25s; }
.tsw-w-seg:hover { opacity:.9; filter:none; }
.tsw-w-seg.is-active { opacity:1; filter:none; }

.tsw-w-chiprow { position:relative; margin:0 -2px; }
.tsw-w-chips { display:flex; gap:6px; overflow-x:auto; padding:2px 2px 8px; scrollbar-width:none; -ms-overflow-style:none; scroll-behavior:smooth; }
.tsw-w-chiprow.has-left::before, .tsw-w-chiprow.has-right::after { content:''; position:absolute; top:0; bottom:12px; width:52px; z-index:1; pointer-events:none; }
.tsw-w-chiprow.has-left::before { left:0; background:linear-gradient(90deg, #fff 55%, rgba(255,255,255,0)); }
.tsw-w-chiprow.has-right::after { right:0; background:linear-gradient(270deg, #fff 55%, rgba(255,255,255,0)); }
.tsw-w-chipnav { position:absolute; top:3px; z-index:2; width:30px; height:30px; padding:0; border-radius:50%; border:1px solid var(--line); background:#fff; color:var(--ink); box-shadow:0 2px 8px rgba(18,21,28,.14); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:background .15s, opacity .15s; }
.tsw-w-chipnav:hover { background:var(--wash); }
.tsw-w-chipnav:focus-visible { outline:2px solid var(--ink); outline-offset:2px; }
.tsw-w-chipnav svg { width:14px; height:14px; }
.tsw-w-chipnav.is-left { left:0; }
.tsw-w-chipnav.is-right { right:0; }
.tsw-w-chips::-webkit-scrollbar { display:none; }
.tsw-w-chip { appearance:none; flex:0 0 auto; display:inline-flex; align-items:center; gap:8px; border:1px solid transparent; background:none; border-radius:999px; padding:6px 12px; font:inherit; font-size:13px; color:var(--muted); cursor:pointer; white-space:nowrap; transition:background .15s, color .15s, border-color .15s; }
.tsw-w-chip:hover { background:var(--wash); color:var(--ink); }
.tsw-w-chip[aria-pressed="true"] { color:var(--ink); font-weight:600; border-color:var(--line); background:#fff; box-shadow:0 1px 2px rgba(18,21,28,.06); }
.tsw-w-dot { width:8px; height:8px; border-radius:50%; flex:0 0 8px; }
.tsw-w-count { font-variant-numeric:tabular-nums; color:var(--muted); font-weight:400; }

.tsw-w-careers { list-style:none; margin:0; padding:0; border-top:1px solid var(--line); }
.tsw-w-career { border-bottom:1px solid var(--line); animation:tsw-w-rise .32s cubic-bezier(.2,.7,.2,1) both; min-width:0; }
.tsw-w-link, .tsw-w-name, .tsw-w-meta, .tsw-w-chip, .tsw-w-empty { text-align:left; }
.tsw-w-side .tsw-w-eyebrow, .tsw-w-side .tsw-w-caption { text-align:center; }
.tsw-w-link { display:grid; grid-template-columns:minmax(0,1fr) auto 16px; align-items:baseline; column-gap:20px; padding:10px 0 9px; color:var(--ink); text-decoration:none; font-size:14px; font-weight:600; line-height:1.3; border-radius:4px; }
.tsw-w-name { min-width:0; overflow-wrap:anywhere; text-wrap:pretty; }
.tsw-w-link:hover .tsw-w-name { text-decoration:underline; text-decoration-color:var(--accent); text-decoration-thickness:2px; text-underline-offset:4px; }
.tsw-w-link:focus-visible, .tsw-w-tab:focus-visible, .tsw-w-chip:focus-visible, .tsw-w-seg:focus-visible, .tsw-w-retry:focus-visible { outline:2px solid var(--ink); outline-offset:2px; }
.tsw-w-arrow { width:16px; height:16px; align-self:center; color:var(--muted); transition:transform .18s, color .18s; }
.tsw-w-link:hover .tsw-w-arrow { transform:translateX(3px); color:var(--ink); }
.tsw-w-meta { display:flex; gap:16px; font-size:12.5px; font-weight:400; color:var(--muted); font-variant-numeric:tabular-nums; white-space:nowrap; }
.tsw-w-meta span { display:inline-block; text-align:right; }
.tsw-w-meta .w-pay { min-width:104px; }
.tsw-w-meta .w-time { min-width:100px; }
.tsw-w-meta .w-courses { min-width:78px; }
.tsw-w.is-narrow .tsw-w-link { grid-template-columns:minmax(0,1fr) 16px; row-gap:4px; padding:11px 0 10px; }
.tsw-w.is-narrow .tsw-w-meta { grid-column:1; grid-row:2; gap:14px; flex-wrap:wrap; white-space:normal; }
.tsw-w.is-narrow .tsw-w-meta span { text-align:left; min-width:0 !important; }
.tsw-w.is-narrow .tsw-w-arrow { grid-column:2; grid-row:1; }
@keyframes tsw-w-rise { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
@keyframes tsw-w-next { from { opacity:0; transform:translateX(28px); } to { opacity:1; transform:none; } }
@keyframes tsw-w-prev { from { opacity:0; transform:translateX(-28px); } to { opacity:1; transform:none; } }
.tsw-w-careers.dir-next .tsw-w-career { animation-name:tsw-w-next; }
.tsw-w-careers.dir-prev .tsw-w-career { animation-name:tsw-w-prev; }
.tsw-w-pager { display:flex; align-items:center; justify-content:flex-end; gap:10px; margin-top:auto; padding-top:8px; }
.tsw-w-range { font-size:12.5px; color:var(--muted); font-variant-numeric:tabular-nums; margin-right:4px; }
.tsw-w-nav { appearance:none; width:30px; height:30px; border-radius:50%; border:1px solid var(--line); background:#fff; color:var(--ink); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:background .15s, border-color .15s, opacity .15s; }
.tsw-w-nav:hover:not(:disabled) { background:var(--wash); border-color:var(--muted); }
.tsw-w-nav:disabled { opacity:.35; cursor:default; }
.tsw-w-nav:focus-visible { outline:2px solid var(--ink); outline-offset:2px; }
.tsw-w-nav svg { width:16px; height:16px; }

.tsw-w-eyebrow { margin:0; font-size:11px; letter-spacing:.09em; text-transform:uppercase; font-weight:600; color:var(--muted); }
.tsw-w:not(.is-narrow) .tsw-w-eyebrow, .tsw-w:not(.is-narrow) .tsw-w-caption { padding:0 20px; }
.tsw-w-caption { margin:3px 0 2px; font-size:12.5px; line-height:1.45; color:var(--muted); text-align:center; text-wrap:pretty; }
.tsw-w-chart { width:100%; display:flex; justify-content:center; align-items:center; min-height:220px; }
.tsw-w:not(.is-narrow) .tsw-w-chart { width:calc(100% + 20px); margin:0 -10px; }
.tsw-w-panel { flex:1 1 auto; min-height:0; border-top:1px solid var(--line); margin-top:2px; padding-top:16px; overflow:auto; }
.tsw-w-empty { margin:0; padding:28px 0; color:var(--muted); font-size:14px; }
.tsw-w-retry { margin-top:8px; }
.tsw-w-foot { padding:0 28px 8px; font-size:11px; color:var(--muted); }
.tsw-w.is-narrow .tsw-w-foot { padding:0 16px 12px; }

@media (prefers-reduced-motion: reduce) {
  .tsw-w-career { animation:none; }
  .tsw-w *, .tsw-w *::before, .tsw-w *::after { transition:none !important; }
}
`;

const TABS = [
  { step: 0, label: 'Careers' },
  { step: 1, label: 'Watch the video' },
  { step: 2, label: 'Ask our AI' },
];

const WideLayout = ({
  sectors,
  loading,
  heading,
  caption,
  onOpenCareer,
  currentStep,
  onStepChange,
  showVideoTab,
  showChatTab,
  showSkilldar,
  skilldarNode,
  videoNode,
  chatNode,
  accent,
  framed,
  fill,
  narrow,
  showPoweredBy,
  poweredBy,
}) => {
  const list = (sectors || []).filter((sector) => sector?.careers?.length > 0);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectorKey = list.map((sector) => sector.name).join('|');
  useEffect(() => setActiveIndex(0), [sectorKey]);
  useEffect(() => {
    const chip = document.querySelector('.tsw-w-chip[aria-pressed="true"]');
    if (chip && chip.scrollIntoView) chip.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [activeIndex]);
  const active = list[Math.min(activeIndex, Math.max(list.length - 1, 0))];

  // The careers of a sector are shown a page at a time, one to a line, five at a time
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState('');
  // arrows to scroll the row of sectors, shown only while there is more of it to see
  const chipsRef = useRef(null);
  const [chipEdge, setChipEdge] = useState({ left: false, right: false });
  useEffect(() => {
    const element = chipsRef.current;
    if (!element) return undefined;
    const update = () => setChipEdge({ left: element.scrollLeft > 2, right: element.scrollLeft + element.clientWidth < element.scrollWidth - 2 });
    update();
    element.addEventListener('scroll', update, { passive: true });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer) observer.observe(element);
    return () => {
      element.removeEventListener('scroll', update);
      if (observer) observer.disconnect();
    };
  });
  const scrollChips = (step) => {
    const element = chipsRef.current;
    if (element) element.scrollBy({ left: step * Math.max(element.clientWidth * 0.75, 120), behavior: 'smooth' });
  };
  const careersOfActive = active?.careers || [];
  const pageSize = 5;
  const pageCount = Math.max(1, Math.ceil(careersOfActive.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = careersOfActive.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const goToPage = (next) => {
    setDirection(next > currentPage ? 'next' : 'prev');
    setPage(next);
  };
  const chooseSector = (index) => {
    setDirection('');
    setPage(0);
    setActiveIndex(index);
  };

  const tabs = TABS.filter((tab) => (tab.step !== 1 || showVideoTab) && (tab.step !== 2 || showChatTab));

  let content;
  if (currentStep === 1 && showVideoTab) {
    content = <div className="tsw-w-panel" role="tabpanel">{videoNode}</div>;
  } else if (currentStep === 2 && showChatTab) {
    content = <div className="tsw-w-panel" role="tabpanel" style={{ height: fill ? undefined : 380 }}>{chatNode}</div>;
  } else if (loading && !list.length) {
    content = <div className="tsw-w-empty" role="status"><CircularProgress size={22} /></div>;
  } else if (!list.length) {
    content = <p className="tsw-w-empty">There are no careers linked to this course yet.</p>;
  } else {
    content = (
      <>
        <div className="tsw-w-spectrum" aria-hidden="true">
          {list.map((sector, index) => (
            <button
              type="button"
              tabIndex={-1}
              key={sector.name}
              className={`tsw-w-seg${index === activeIndex ? ' is-active' : ''}`}
              style={{ flexGrow: sector.careers.length, background: sector.color || 'var(--ink)' }}
              onClick={() => chooseSector(index)}
              title={sector.name}
              aria-label={sector.name}
            />
          ))}
        </div>
        <div className={`tsw-w-chiprow${chipEdge.left ? ' has-left' : ''}${chipEdge.right ? ' has-right' : ''}`}>
          {chipEdge.left && (
            <button type="button" className="tsw-w-chipnav is-left" aria-label="Show earlier sectors" onClick={() => scrollChips(-1)}>
              <ChevronIcon flip />
            </button>
          )}
          {chipEdge.right && (
            <button type="button" className="tsw-w-chipnav is-right" aria-label="Show more sectors" onClick={() => scrollChips(1)}>
              <ChevronIcon />
            </button>
          )}
          <div className="tsw-w-chips" ref={chipsRef}>
            {list.map((sector, index) => (
              <button
                type="button"
                key={sector.name}
                className="tsw-w-chip"
                aria-pressed={index === activeIndex}
                onClick={() => chooseSector(index)}
              >
                <span className="tsw-w-dot" style={{ background: sector.color || 'var(--ink)' }} />
                {sector.name}
                <span className="tsw-w-count">{sector.careers.length}</span>
              </button>
            ))}
          </div>
        </div>
        <ul className={`tsw-w-careers${direction ? ` dir-${direction}` : ''}`} key={`${active?.name}-${currentPage}`}>
          {visible.map((career, index) => {
            const salary = money(career.salary);
            const courses = Number(career.coursesAvailable);
            return (
              <li className="tsw-w-career" key={career.id || career.name} style={{ animationDelay: `${index * 24}ms` }}>
                <a
                  className="tsw-w-link"
                  href={`#career-${career.id}`}
                  onClick={(event) => {
                    event.preventDefault();
                    onOpenCareer(career.id);
                  }}
                >
                  <span className="tsw-w-name">{career.name}</span>
                  <span className="tsw-w-meta">
                    <span className="w-pay">{salary ? `${salary} a year` : ''}</span>
                    <span className="w-time">{career.transferWindow || ''}</span>
                    <span className="w-courses">{courses > 0 ? `${courses} ${courses > 1 ? 'courses' : 'course'}` : ''}</span>
                  </span>
                  <ArrowIcon className="tsw-w-arrow" />
                </a>
              </li>
            );
          })}
        </ul>
        {pageCount > 1 && (
          <div className="tsw-w-pager">
            <span className="tsw-w-range" aria-live="polite">
              {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, careersOfActive.length)} of {careersOfActive.length}
            </span>
            <button type="button" className="tsw-w-nav" aria-label="Previous careers" disabled={currentPage === 0} onClick={() => goToPage(currentPage - 1)}>
              <ChevronIcon flip />
            </button>
            <button type="button" className="tsw-w-nav" aria-label="Next careers" disabled={currentPage >= pageCount - 1} onClick={() => goToPage(currentPage + 1)}>
              <ChevronIcon />
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className={`tsw-w${framed ? ' is-framed' : ''}${fill ? ' is-fill' : ''}${narrow ? ' is-narrow' : ''}`}
      style={accent ? { '--accent': accent } : undefined}
    >
      <style>{STYLES}</style>
      <div className="tsw-w-body">
        <div className="tsw-w-main">
          <div className="tsw-w-head">
            <h2 className="tsw-w-title">{heading}</h2>
            {tabs.length > 1 && (
              <div className="tsw-w-tabs" role="tablist">
                {tabs.map((tab) => (
                  <button
                    type="button"
                    role="tab"
                    key={tab.step}
                    className="tsw-w-tab"
                    aria-selected={currentStep === tab.step}
                    onClick={() => onStepChange(tab.step)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {content}
        </div>
        {showSkilldar && (
          <aside className="tsw-w-side" aria-label="Skilldar">
            <p className="tsw-w-eyebrow">Skilldar</p>
            <p className="tsw-w-caption">{caption}</p>
            <div className="tsw-w-chart">{skilldarNode}</div>
          </aside>
        )}
      </div>
      {showPoweredBy && <div className="tsw-w-foot">{poweredBy}</div>}
    </div>
  );
};

WideLayout.propTypes = {
  sectors: PropTypes.array,
  loading: PropTypes.bool,
  heading: PropTypes.string,
  caption: PropTypes.string,
  onOpenCareer: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  onStepChange: PropTypes.func.isRequired,
  showVideoTab: PropTypes.bool,
  showChatTab: PropTypes.bool,
  showSkilldar: PropTypes.bool,
  skilldarNode: PropTypes.node,
  videoNode: PropTypes.node,
  chatNode: PropTypes.node,
  accent: PropTypes.string,
  framed: PropTypes.bool,
  fill: PropTypes.bool,
  narrow: PropTypes.bool,
  showPoweredBy: PropTypes.bool,
  poweredBy: PropTypes.string,
};

export default WideLayout;
